"use client";

import { FormEvent, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  SiteHeader,
  type SiteHeaderItem,
} from "@/components/layout/site-header";
import styles from "../../login/login.module.css";
import { getApiUrl } from "@/lib/api";

type LoginStatus = {
  message: string;
  tone: "error" | "info" | "success";
};

type AuthResponse = {
  status?: string;
  message?: string;
  data?: {
    token?: string;
    member?: unknown;
  };
};

const HEADER_ITEMS: readonly SiteHeaderItem[] = [
  { href: "/", label: "Trang chủ" },
  { href: "/hoc-tap/dashboard", label: "Cổng đào tạo" },
  { href: "/login", label: "Cổng dự thi", isAction: true },
];

export function HocTapLoginClient() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<LoginStatus | null>(null);

  // Form state
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");

  const apiUrl = getApiUrl();

  useEffect(() => {
    const memberToken = localStorage.getItem("member_token");
    if (memberToken) {
      fetch(`${apiUrl}/api/v1/member/auth/me`, {
        headers: { Authorization: `Bearer ${memberToken}` },
      })
        .then((res) => {
          if (res.ok) {
            router.push("/hoc-tap/dashboard");
          } else {
            localStorage.removeItem("member_token");
            localStorage.removeItem("member_user");
            window.dispatchEvent(new Event("auth-change"));
          }
        })
        .catch(() => {});
    }
  }, [router, apiUrl]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!identifier.trim() || !password) {
      setStatus({
        message: "Vui lòng nhập Email / Mã số sinh viên và Mật khẩu.",
        tone: "error",
      });
      return;
    }

    setIsLoading(true);
    setStatus(null);

    try {
      const response = await fetch(`${apiUrl}/api/v1/member/auth/login`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
          password,
        }),
      });
      const data = (await response.json()) as AuthResponse;

      if (!response.ok) {
        throw new Error(
          data.message || "Tài khoản hoặc mật khẩu không chính xác."
        );
      }

      if (!data.data?.token) {
        throw new Error("Phản hồi đăng nhập không hợp lệ.");
      }

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.setItem("member_token", data.data.token);
      localStorage.setItem("member_user", JSON.stringify(data.data.member ?? null));
      window.dispatchEvent(new Event("auth-change"));

      setStatus({
        message: "Đăng nhập thành công! Đang chuyển hướng về Dashboard...",
        tone: "success",
      });
      window.setTimeout(() => router.push("/hoc-tap/dashboard"), 600);
    } catch (error: unknown) {
      setStatus({
        message:
          error instanceof Error
            ? error.message
            : "Không thể kết nối đến máy chủ. Vui lòng thử lại sau.",
        tone: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      <a className={styles.skipLink} href="#hoc-tap-form">
        Đi đến biểu mẫu đăng nhập
      </a>

      <SiteHeader />

      <main className={styles.designStage}>
        <section
          className={styles.hero}
          id="hoc-tap-home"
          aria-labelledby="hoc-tap-hero-title"
        >
          <h1 className={styles.srOnly} id="hoc-tap-hero-title">
            Cổng Đào Tạo Sinh Viên Panasonic – Đăng nhập tài khoản cá nhân
          </h1>

          <form
            className={styles.loginForm}
            id="hoc-tap-form"
            onSubmit={handleSubmit}
          >
            <div className={styles.field}>
              <label htmlFor="member-id">Tài khoản</label>
              <input
                id="member-id"
                name="identifier"
                type="text"
                autoComplete="username"
                placeholder="Mã sinh viên hoặc Email"
                value={identifier}
                disabled={isLoading}
                onChange={(e) => setIdentifier(e.target.value)}
                required
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="member-password">Mật khẩu</label>
              <input
                id="member-password"
                name="password"
                type="password"
                autoComplete="current-password"
                placeholder="Mật khẩu của bạn"
                value={password}
                disabled={isLoading}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className={styles.formOptions}>
              <label className={styles.checkLabel}>
                <input name="remember" type="checkbox" disabled={isLoading} />
                <span>Lưu mật khẩu</span>
              </label>
              <button
                className={styles.forgotButton}
                type="button"
                onClick={() =>
                  setStatus({
                    message:
                      "Tài khoản sinh viên do nhà trường cấp. Vui lòng liên hệ Giảng viên hoặc Ban Giám Hiệu để được hỗ trợ.",
                    tone: "info",
                  })
                }
              >
                Quên mật khẩu
              </button>
            </div>

            <label className={`${styles.checkLabel} ${styles.termsLabel}`}>
              <input
                name="terms"
                type="checkbox"
                defaultChecked
                disabled={isLoading}
                required
              />
              <span>
                Tôi đồng ý với{" "}
                <a
                  href="https://docs.google.com/document/d/1DzcrySd5lo7bIo1YzEIWNLjY2NYw6FdIij_hBObqklE/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Điều khoản sử dụng và Chính sách bảo vệ dữ liệu cá nhân của Panasonic
                </a>
              </span>
            </label>

            <button
              className={styles.loginSubmit}
              type="submit"
              disabled={isLoading}
              aria-busy={isLoading}
              aria-label={isLoading ? "Đang đăng nhập" : "Đăng nhập học tập"}
            >
              <span className={styles.srOnly}>
                {isLoading ? "Đang đăng nhập..." : "Đăng nhập"}
              </span>
            </button>

            <p
              style={{
                marginTop: "16px",
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.75)",
                textAlign: "center",
                lineHeight: "1.4",
              }}
            >
              Chưa có tài khoản?{" "}
              <Link href="/hoc-tap/register" style={{ color: "#31ecff", fontWeight: "bold", textDecoration: "underline" }}>Đăng ký thí sinh tự do</Link>
            </p>
          </form>
        </section>
      </main>

      {status && (
        <div
          className={`${styles.loginStatus} ${styles[status.tone]}`}
          role="status"
          aria-live="polite"
        >
          {status.message}
        </div>
      )}
    </div>
  );
}
